import { useMemo } from 'react'

import {
  EditorialCellPlacement,
  EditorialPlacement,
  EditorialRowLegacyPlacement,
  EditorialRowPlacement,
} from '../../../../../types/graphql/collection-view'
import { TileVariantGroupCollectionScope } from '../sections/CollectionGridSection/Tiles/ProductGroupTile'
import { TileItem, TitleTile } from '../sections/CollectionGridSection/Tiles/types'

import { CollectionItemVariantGroup } from './useCollectionGridSectionItems'

export type UseEditorializedCollectionsProps = {
  collections: Array<{
    collectionScope: TileVariantGroupCollectionScope
    products: CollectionItemVariantGroup[]
    editorialCellPlacements: readonly EditorialCellPlacement[]
    editorialRows: readonly EditorialRowLegacyPlacement[]
    editorialOverrides: EditorialPlacement[]
    title: string | undefined
    subtitle: string | undefined
    numItems: number
  }>
  ignoreEditorials: boolean
}

export type UseEditorializedCollectionsResults = Array<{
  collectionScope: TileVariantGroupCollectionScope
  items: {
    mobile: TileItem[]
    desktop: TileItem[]
  }
}>

const mapDesktopTileAlignmentToOffset = (tile: EditorialCellPlacement) => {
  const alignment = tile.alignmentDesktop

  if (alignment === '1') {
    return 0
  } else if (alignment === '2') {
    return 1
  } else if (alignment === '3') {
    return 2
  }

  return 3
}

/**
 * Get fallback rowsToSkip values based on editorial type and position.
 * For VideoSection (which doesn't have rowsToSkip properties), we use row fallbacks
 * to maintain backwards compatibility
 */
const getFallbackRowsToSkip = (item: TileItem, isFirst: boolean, isMobile: boolean): number => {
  // Cell editorials get cell-specific fallbacks
  if (item.__typename === 'EditorialCellPlacement') {
    if (isMobile) {
      return isFirst ? 1 : 2
    } else {
      return isFirst ? 0 : 1
    }
  }

  // Row editorials (EditorialRowPlacement and VideoSection) get row fallbacks
  // VideoSection uses row fallbacks for backward compatibility
  if (isMobile) {
    return isFirst ? 3 : 2
  } else {
    return isFirst ? 2 : 1
  }
}

const getRowsToSkip = (item: TileItem, isFirst: boolean, isMobile: boolean): number => {
  if (isMobile && 'rowsToSkipMobile' in item && item.rowsToSkipMobile !== null) {
    return item.rowsToSkipMobile
  } else if ('rowsToSkipDesktop' in item && item.rowsToSkipDesktop !== null) {
    return item.rowsToSkipDesktop
  }

  return getFallbackRowsToSkip(item, isFirst, isMobile)
}

/**
 * Merge products with all editorials (cells, rows, and videos).
 * NOTE video sections are treated as rows for backwards compatibility
 */
const mergeProductsAndEditorials = ({
  editorials,
  pinnedEditorials,
  collectionScope,
  products,
  itemsPerRow,
  isMobile,
}: {
  editorials: TileItem[]
  pinnedEditorials: TileItem[]
  collectionScope: UseEditorializedCollectionsProps['collections'][number]['collectionScope']
  products: CollectionItemVariantGroup[]
  itemsPerRow: number
  isMobile: boolean
}) => {
  const mergedTileItems: TileItem[] = []
  let productsIndex: number = 0

  editorials.forEach((editorial, editorialsIndex) => {
    let tilesToAddPriorToEditorial = 0
    let tilesToAddAfterEditorial = 0
    const isFirstEditorial = editorialsIndex === 0

    const requestedCellWidth = 'cellWidth' in editorial ? editorial.cellWidth ?? 1 : 4
    const cellWidth = requestedCellWidth > itemsPerRow ? itemsPerRow : requestedCellWidth

    // fallback offset to 0 for row editorials
    const requestedColumnOffset =
      editorial.__typename === 'EditorialCellPlacement'
        ? mapDesktopTileAlignmentToOffset(editorial)
        : 0
    const columnOffset = Math.min(requestedColumnOffset, itemsPerRow - 1)

    const rowsToSkip = getRowsToSkip(editorial, isFirstEditorial, isMobile)

    tilesToAddPriorToEditorial = rowsToSkip * itemsPerRow

    // Desktop-specific adjustments for column offset and trailing fill
    // Mobile editorials always fill the row, so no adjustments needed
    if (!isMobile) {
      // Add column offset to shift editorial rightward
      tilesToAddPriorToEditorial = tilesToAddPriorToEditorial + columnOffset

      // Calculate trailing products to fill remainder of row
      if (columnOffset + cellWidth <= itemsPerRow) {
        // Editorial fits in row - fill any remaining space
        const trailingTiles = (tilesToAddPriorToEditorial + cellWidth) % itemsPerRow
        tilesToAddAfterEditorial = trailingTiles > 0 ? itemsPerRow - trailingTiles : 0
      } else {
        // Editorial too wide, starts new row - fill remainder of that row
        tilesToAddAfterEditorial = itemsPerRow - cellWidth
      }
    }

    // Place products and editorial according to calculated positions
    if (products.length >= productsIndex + tilesToAddPriorToEditorial) {
      for (
        let loopCounter = 0;
        loopCounter < tilesToAddPriorToEditorial;
        loopCounter++, productsIndex++
      ) {
        const next = products[productsIndex]
        if (next) mergedTileItems.push({ ...next, collectionScope })
      }
      mergedTileItems.push(editorial)
      for (
        let loopCounter = 0;
        loopCounter < tilesToAddAfterEditorial;
        loopCounter++, productsIndex++
      ) {
        const next = products[productsIndex]
        if (next) mergedTileItems.push({ ...next, collectionScope })
      }
    } else {
      // Not enough products - just add remaining products without the editorial
      for (productsIndex; productsIndex < products.length; productsIndex++) {
        const next = products[productsIndex]
        if (next) mergedTileItems.push({ ...next, collectionScope })
      }
    }
  })

  // Add any remaining products that weren't placed
  for (productsIndex; productsIndex < products.length; productsIndex++) {
    const next = products[productsIndex]
    if (next) mergedTileItems.push({ ...next, collectionScope })
  }

  pinnedEditorials.forEach(pinnedEditorial => {
    mergedTileItems.push(pinnedEditorial)
  })

  return mergedTileItems
}

// Combine editorialCellPlacements and editorialRows (EditorialRowPlacement or VideoSection)
const buildCombinedEditorialsArray = (
  collection: UseEditorializedCollectionsProps['collections'][number],
  collectionIsFullyLoaded: boolean
) => {
  //if editorials has content, we override the editorials with this content and respect their order.
  const editorialsAlreadyCombined = collection.editorialOverrides.length > 0
  if (editorialsAlreadyCombined) {
    return {
      editorials: collection.editorialOverrides.filter(editorial =>
        editorial.__typename === 'EditorialCellPlacement' ? editorial.pinToBottom !== 'yes' : true
      ) as EditorialCellPlacement[] | EditorialRowPlacement[],
      pinnedEditorials: collection.editorialOverrides.filter(editorial =>
        editorial.__typename === 'EditorialCellPlacement' ? editorial.pinToBottom == 'yes' : false
      ) as EditorialCellPlacement[],
    }
  }

  const editorials: TileItem[] = []
  const pinnedEditorials: TileItem[] = []

  const maxLength = Math.max(
    collection.editorialCellPlacements.length,
    collection.editorialRows.length
  )

  for (let i = 0; i < maxLength; i++) {
    const cellPlacement = collection.editorialCellPlacements[i]
    if (cellPlacement) {
      if (cellPlacement.pinToBottom === 'yes') {
        if (collectionIsFullyLoaded) {
          pinnedEditorials.push(cellPlacement)
        }
      } else {
        editorials.push(cellPlacement)
      }
    }

    const rowEditorial = collection.editorialRows[i]
    if (rowEditorial) {
      editorials.push(rowEditorial)
    }
  }

  return { editorials, pinnedEditorials }
}

const buildTilesForLayout = ({
  collection,
  itemsPerRow,
  isMobile,
  ignoreEditorials,
}: {
  collection: UseEditorializedCollectionsProps['collections'][number]
  itemsPerRow: number
  isMobile: boolean
  ignoreEditorials: boolean
}) => {
  const collectionIsFullyLoaded = collection.products.length === collection.numItems

  const { editorials, pinnedEditorials } = buildCombinedEditorialsArray(
    collection,
    collectionIsFullyLoaded
  )

  return mergeProductsAndEditorials({
    editorials: ignoreEditorials ? [] : editorials,
    pinnedEditorials,
    itemsPerRow,
    products: collection.products,
    isMobile,
    collectionScope: collection.collectionScope,
  })
}

export const useEditorializedCollections = ({
  collections,
  ignoreEditorials,
}: UseEditorializedCollectionsProps) => {
  const editorializedCollections = useMemo(() => {
    const editorializedCollections: UseEditorializedCollectionsResults = []

    collections.forEach(collection => {
      if (collection.products.length === 0) return

      const mobileTileItems = buildTilesForLayout({
        collection,
        itemsPerRow: 2,
        isMobile: true,
        ignoreEditorials,
      })

      const desktopTileItems = buildTilesForLayout({
        collection,
        itemsPerRow: 4,
        isMobile: false,
        ignoreEditorials,
      })

      // Add title tile to the front if present
      if (collection.title && !ignoreEditorials) {
        const titleTile: TitleTile = {
          __typename: 'TitleTile' as const,
          id: `TitleTile:${collection.collectionScope.collectionHandle}`,
          title: collection.title,
          subtitle: collection.subtitle,
        }
        mobileTileItems.unshift(titleTile)
        desktopTileItems.unshift(titleTile)
      }

      editorializedCollections.push({
        collectionScope: collection.collectionScope,
        items: {
          mobile: mobileTileItems,
          desktop: desktopTileItems,
        },
      })
    })

    return editorializedCollections
  }, [collections, ignoreEditorials])

  return editorializedCollections
}
